// EWM-641 — FE mirror of the BFF rule (backend `utils/isBackorderRestricted`).
// A customer is back-order RESTRICTED when their business unit does NOT allow
// back-orders, OR is a cash account (`payment-terms` = "P", case-insensitive).
// Restricted customers are gated against available stock; everyone else orders
// freely. The BFF `checkout` action is the authoritative gate — this is the UX
// side (disable Proceed + show a general out-of-stock message).
export const isBackorderRestricted = (
  businessUnit?: { backOrdersAllowed?: boolean; paymentTerms?: string } | null,
): boolean => {
  const backOrdersAllowed = businessUnit?.backOrdersAllowed === true;
  const isCashAccount = (businessUnit?.paymentTerms ?? '').trim().toLowerCase() === 'p';
  return !backOrdersAllowed || isCashAccount;
};

/**
 * Whether a cart line's quantity exceeds available stock — evaluated ONLY for
 * restricted customers.
 *
 * EWM-782 — `stockCovered` (the EWM-775 `inStock` custom flag, stamped by the
 * BFF from LIVE warehouse stock on every cart mutation) wins when present: it
 * encodes exactly this test (`availableQuantity ≥ quantity`) against fresh
 * data, and matches the BFF checkout gate by construction. The
 * `inStock`/`maxQuantity` fallback reads the commercetools ADD-TIME variant
 * snapshot, which goes stale when stock changes after the line was added —
 * the cause of the "cart says in stock, checkout rejects" incident.
 *
 * Fallback semantics unchanged: `inStock === false` ⇒ 0 available; an
 * in-stock line with an unknown `maxQuantity` is treated as 0 available
 * (fail-closed).
 */
export const isLineOverAvailableStock = (item: {
  quantity?: number;
  inStock?: boolean;
  maxQuantity?: number;
  stockCovered?: boolean;
}): boolean => {
  if (typeof item.stockCovered === 'boolean') {
    return !item.stockCovered;
  }
  const available = item.inStock ? (item.maxQuantity ?? 0) : 0;
  return (item.quantity ?? 0) > available;
};
